import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { catchError, firstValueFrom, of, tap } from 'rxjs';
import { Modal } from '../shared/models/component/modal.model';
import { ModalService } from '../shared/services/modal/modal.service';
import { WebsocketService } from '../shared/services/websocket/websocket.service';
import { AccessApp } from './models/accessApp.model';
import NavbarItem from './models/navbar-item.model';
import { UserNavigationHistory } from './models/user_navigation_history.model';
import { EditProfile, Preference, UserData } from './models/userData.model';
import { AppwriteService } from './services/appwrite/appwrite';
import { QueryService } from './services/query/query.service';

export type HeadbandProps = {
  id: number;
  title: string;
  type: Modal.Type;
  content: string;
  activated: boolean;
  global: boolean;
};

type UserState = {
  user: UserData;
  access_app: AccessApp[];
  interval: NodeJS.Timeout | null;
  isAuthentified: boolean;
  alertDev: HeadbandProps[];
  mailPreferences: Preference.MailSetting[];
  loading: boolean;
};

export const initialUserState: UserState = {
  user: {
    id: 0,
    civ: 'M',
    firstname: '',
    lastname: '',
    mail: '',
    phone: '',
    mobile_phone: '',
    photo: '',
    is_admin_paddock: false,
    affaire: [],
    user_job: [],
    in_bilat: 'false',
    admin_affaire_right: 'not-allowed',
    admin_user_account_right: 'not-allowed',
    admin_brand_right: 'not-allowed',
    admin_business_introducer_right: 'not-allowed',
    admin_job_right: 'not-allowed',
    admin_phone_book_right: 'not-allowed',
    admin_model_right: 'not-allowed',
    admin_plaque_right: 'not-allowed',
    admin_user_tracking_right: 'not-allowed',
    business_introducer: [],
    type_commercial: '',
  },
  access_app: [],
  interval: null,
  isAuthentified: false,
  alertDev: [],
  mailPreferences: [],
  loading: true,
};

export const userStore = signalStore(
  { providedIn: 'root' },
  withState(initialUserState),
  withMethods((_store, queryService = inject(QueryService), appwriteService = inject(AppwriteService)) => {
    let refreshInFlight: Promise<void> | undefined;

    return {
      refreshAppwriteJwt: (): Promise<void> => {
        if (localStorage.getItem('SSO') !== 'true') return Promise.resolve();
        if (!refreshInFlight) {
          refreshInFlight = (async () => {
            try {
              const { jwt } = await appwriteService.accountInstance.createJWT({ duration: 3600 });
              // Resolve only after the API has replaced the HttpOnly cookie.
              await firstValueFrom(queryService.refreshTokenexpiration(jwt));
            } finally {
              refreshInFlight = undefined;
            }
          })();
        }
        return refreshInFlight;
      },
    };
  }),
  withMethods(
    (
      store,
      queryService = inject(QueryService),
      webSocketService = inject(WebsocketService),
      router = inject(Router),
      modalService = inject(ModalService),
      appwriteService = inject(AppwriteService),
    ) => ({
      setUserInfos: (userInfo?: UserData) => {
        patchState(store, { loading: true });
        if (userInfo) {
          patchState(store, { user: userInfo, isAuthentified: true, loading: false });
          return;
        }
        return queryService.getUserInfos().pipe(
          tap((response) => {
            if (response.body) {
              sessionStorage.setItem('userSession', JSON.stringify(response.body));
              patchState(store, { user: response.body, isAuthentified: true, loading: false });
              if (!webSocketService.getSocket()) {
                webSocketService.connect(response.body.id);
              }
            } else {
              throw new Error('Erreur lors de la récupération des informations utilisateur');
            }
          }),
          catchError((error) => {
            patchState(store, { loading: false });
            localStorage.clear();
            sessionStorage.clear();
            console.error('Erreur lors de la récupération des informations utilisateur', error);
            modalService.openAlert('error', 'Erreur', "Erreur lors de la récupération des données de l'utilisateur connecté", () => {
              modalService.closeAlert();
              patchState(store, initialUserState);
              router.navigate(['/']);
            });
            return of(null);
          }),
        );
      },
      setAccessApp: (accessAppInfo?: AccessApp[]) => {
        patchState(store, { loading: true });
        if (accessAppInfo) {
          patchState(store, { access_app: accessAppInfo });
          patchState(store, { isAuthentified: true });
          patchState(store, { loading: false });
          return;
        }
        return queryService.getAccessApp().pipe(
          tap((response) => {
            if (response.body) {
              sessionStorage.setItem('accessApp', JSON.stringify(response.body));
              patchState(store, { access_app: response.body, isAuthentified: true, loading: false });
            } else {
              throw new Error('Erreur lors de la récupération des informations utilisateur');
            }
          }),
          catchError((error) => {
            patchState(store, { loading: false });
            localStorage.clear();
            sessionStorage.clear();
            console.error('Erreur lors de la récupération des informations utilisateur', error);
            modalService.openAlert('error', 'Erreur', "Erreur lors de la récupération des données d'accès de l'utilisateur connecté", () => {
              modalService.closeAlert();
            });
            return of(null);
          }),
        );
      },
      login: (isAuthentified: boolean) => {
        patchState(store, { isAuthentified: isAuthentified });
        localStorage.setItem('isAuthentified', JSON.stringify(isAuthentified));
      },
      logout: () => {
        queryService.disconnect().subscribe({
          next: () => {
            localStorage.removeItem('isAuthentified');
            localStorage.removeItem('SSO');
            localStorage.removeItem('getTokenMail');
            patchState(store, initialUserState);
            if (localStorage.getItem('sso') === 'true') {
              appwriteService.logout();
            }
            router.navigate(['/']);
            modalService.openMiniHeadband('', '', '', () => {
              modalService.closeMiniHeadband();
            });
            modalService.closeMiniHeadband();
          },
          error: (error) => {
            console.error('Erreur lors de la déconnexion', error);
            if (store.interval()) clearTimeout(store.interval()!);
            localStorage.removeItem('isAuthentified');
            patchState(store, initialUserState);
            router.navigate(['/']);
          },
        });
      },
      getMenuOfApp: (nameApp: string): NavbarItem[] => {
        const accessApp = store.access_app();
        return accessApp.find((app) => app.name_application === nameApp)?.menu || [];
      },
      postUserNavigationHistory: (data: UserNavigationHistory.Create) => {
        queryService.postUserNavigationHistory(data).subscribe({
          error: (error) => {
            console.error("Erreur lors de l'enregistrement de l'historique de navigation", error);
            modalService.openAlert('error', 'Erreur', "Erreur lors de l'enregistrement de la navigation", () => {
              modalService.closeAlert();
            });
          },
        });
      },
      getAccessApp: (name_app: string) => {
        return store.access_app().find((app) => app.name_application === name_app);
      },
      editProfile(paylaod: EditProfile) {
        patchState(store, { loading: true });
        queryService.updateProdile(paylaod).subscribe({
          next: (response) => {
            if (response.body) {
              patchState(store, { user: { ...store.user(), ...response.body } });
              modalService.openAlert('success', 'Succès', 'Profil modifié avec succès', () => {
                modalService.closeAlert();
                window.location.reload();
              });
            } else {
              throw new Error('Erreur lors de la modification du profil');
            }
          },
          error: (error) => {
            console.error('Erreur lors de la modification du profil', error);
            modalService.openAlert('error', 'Erreur', 'Erreur lors de la modification du profil', () => {
              modalService.closeAlert();
            });
          },
          complete: () => {
            patchState(store, { loading: false });
          },
        });
      },
      getAlertDev(id_application: number) {
        patchState(store, { loading: true });
        queryService.getAlertDev(id_application).subscribe({
          next: (response) => {
            if (response.body) {
              patchState(store, { alertDev: response.body });
            } else {
              throw new Error('Erreur lors de la récupération des alertes de développement');
            }
          },
          error: (error) => {
            console.error('Erreur lors de la récupération des alertes de développement', error);
            modalService.openAlert('error', 'Erreur', 'Erreur lors de la récupération des alertes de développement', () => {
              modalService.closeAlert();
            });
          },
          complete: () => {
            patchState(store, { loading: false });
          },
        });
      },
      onAlertDevCLick(event: boolean, id: number) {
        queryService.setAlertDevPreference({ id_alert_dev: id, hide: true }).subscribe({
          next: () => {
            patchState(store, {
              alertDev: store.alertDev().filter((alert) => alert.id !== id),
            });
          },
          error: (error) => {
            console.error("Erreur lors de la modification de la préférence de l'alerte", error);
            modalService.openAlert('error', 'Erreur', "Erreur lors de la modification de la préférence de l'alerte", () => {
              modalService.closeAlert();
            });
          },
        });
      },
      getMailPreferences() {
        patchState(store, { loading: true });
        queryService.getMailPreferences().subscribe({
          next: (response) => {
            if (response.body) {
              patchState(store, { mailPreferences: response.body });
            } else {
              throw new Error('Erreur lors de la récupération des préférences email');
            }
          },
          error: (error) => {
            console.error('Erreur lors de la récupération des préférences email', error);
            modalService.openAlert('error', 'Erreur', 'Erreur lors de la récupération des préférences email', () => {
              modalService.closeAlert();
            });
          },
          complete: () => {
            patchState(store, { loading: false });
          },
        });
      },
      updatePreference(payload: Preference.Update) {
        patchState(store, { loading: true });
        queryService.updatePreference(payload).subscribe({
          next: () => {
            modalService.openAlert('success', 'Succès', 'Préférence mise à jour avec succès', () => {
              modalService.closeAlert();
            });
          },
          error: (error) => {
            console.error('Erreur lors de la mise à jour de la préférence', error);
            modalService.openAlert('error', 'Erreur', 'Erreur lors de la mise à jour de la préférence', () => {
              modalService.closeAlert();
            });
          },
          complete: () => {
            patchState(store, { loading: false });
          },
        });
      },
      refreshTokenInfo() {
        queryService.refreshTokenInfo().subscribe({
          next: () => console.log('Refresh token succeeded.'),
          error: (err) => console.error('Failed to refresh token:', err),
        });
      },
      findAffaireById(id: number) {
        return store.user().affaire.find((affaire) => affaire.id === id);
      },
      setBiltatMode(mode: string) {
        patchState(store, { user: { ...store.user(), in_bilat: mode } });
      },
      addBookmark(id_application: number) {
        queryService.addBookmark(id_application).subscribe({
          next: () => {
            const accessApp = store.access_app().find((app) => app.id_application === id_application);
            if (accessApp) {
              accessApp.is_bookmarked = true;
              patchState(store, { access_app: [...store.access_app()] });
            }
          },
          error: (error) => {
            console.error('Erreur lors de l’ajout du favoris', error);
            modalService.openAlert('error', 'Erreur', 'Erreur lors de l’ajout du favoris', () => {
              modalService.closeAlert();
            });
          },
        });
      },
      deleteBookmark(id: number) {
        queryService.deleteBookmark(id).subscribe({
          next: () => {
            const accessApp = store.access_app().find((app) => app.id_application === id);
            if (accessApp) {
              accessApp.is_bookmarked = false;
              patchState(store, { access_app: [...store.access_app()] });
            }
          },
          error: (error) => {
            console.error('Erreur lors de la suppression du favoris', error);
            modalService.openAlert('error', 'Erreur', 'Erreur lors de la suppression du favoris', () => {
              modalService.closeAlert();
            });
          },
        });
      },
    }),
  ),
  withComputed((store) => ({
    fullNavbarItem: computed(() => [
      ...store.access_app().flatMap((app) => app.menu),
      {
        label: 'Actualités',
        link: '/portail/actualites',
        icon: 'M13.86 1.989a2.61 2.61 0 0 0-3.726 0L.768 11.527a2.73 2.73 0 0 0 0 3.795l6.684 6.808a2.618 2.618 0 0 0 1.74.664h5.613a2.616 2.616 0 0 0 1.872-.791l6.554-6.675a2.726 2.726 0 0 0 0-3.795l-9.37-9.544zM13.6 19.41l-.935.95a.372.372 0 0 1-.268.114h-.8a.376.376 0 0 1-.247-.096l-.954-.97a.39.39 0 0 1 0-.542l1.337-1.36a.37.37 0 0 1 .53 0l1.338 1.36a.389.389 0 0 1 0 .544zm0-5.711l-3.737 3.808a.374.374 0 0 1-.268.11h-.8a.376.376 0 0 1-.25-.092l-.95-.97a.391.391 0 0 1 0-.544l4.139-4.214a.372.372 0 0 1 .53 0l1.338 1.362a.386.386 0 0 1 0 .54zm0-5.707l-6.54 6.66a.372.372 0 0 1-.268.113h-.8a.373.373 0 0 1-.25-.094L4.79 13.7a.388.388 0 0 1 0-.54l6.943-7.07a.372.372 0 0 1 .53 0l1.338 1.36a.389.389 0 0 1 0 .543z',
        ratio: '0 0 24 24',
      },
      {
        label: 'Trombinoscope',
        link: '/portail/utilisateur',
        icon: 'M12 5.5A3.5 3.5 0 0 1 15.5 9a3.5 3.5 0 0 1-3.5 3.5A3.5 3.5 0 0 1 8.5 9A3.5 3.5 0 0 1 12 5.5M5 8c.56 0 1.08.15 1.53.42c-.15 1.43.27 2.85 1.13 3.96C7.16 13.34 6.16 14 5 14a3 3 0 0 1-3-3a3 3 0 0 1 3-3m14 0a3 3 0 0 1 3 3a3 3 0 0 1-3 3c-1.16 0-2.16-.66-2.66-1.62a5.536 5.536 0 0 0 1.13-3.96c.45-.27.97-.42 1.53-.42M5.5 18.25c0-2.07 2.91-3.75 6.5-3.75s6.5 1.68 6.5 3.75V20h-13v-1.75M0 20v-1.5c0-1.39 1.89-2.56 4.45-2.9c-.59.68-.95 1.62-.95 2.65V20H0m24 0h-3.5v-1.75c0-1.03-.36-1.97-.95-2.65c2.56.34 4.45 1.51 4.45 2.9V20Z',
        ratio: '0 0 24 24',
      },
      {
        label: 'Numéros utiles',
        link: '/portail/annuaire',
        icon: 'M2 21q-.825 0-1.412-.587Q0 19.825 0 19V5q0-.825.588-1.413Q1.175 3 2 3h20q.825 0 1.413.587Q24 4.175 24 5v14q0 .825-.587 1.413Q22.825 21 22 21Zm7-7q1.25 0 2.125-.875T12 11q0-1.25-.875-2.125T9 8q-1.25 0-2.125.875T6 11q0 1.25.875 2.125T9 14Zm10 4l2-2l-1.5-2h-1.65q-.15-.45-.25-.963q-.1-.512-.1-1.037q0-.525.1-1.012q.1-.488.25-.988h1.65L21 8l-2-2q-1.35 1.05-2.175 2.662Q16 10.275 16 12t.825 3.337Q17.65 16.95 19 18ZM2.1 19h13.8q-1.05-1.875-2.9-2.938Q11.15 15 9 15t-4 1.062Q3.15 17.125 2.1 19Z',
        ratio: '0 0 24 24',
      },
    ]),
    isLeadDev: computed(
      () =>
        store.user().id === 271 ||
        store.user().id === 272 ||
        store.user().id === 961 ||
        store.user().id === 443 ||
        store.user().id === 838 ||
        store.user().id === 898 ||
        store.user().id === 1404,
    ),
    hasAccessToAdminGate: computed(
      () =>
        store.user().is_admin_paddock ||
        store.user().admin_affaire_right !== 'not-allowed' ||
        store.user().admin_brand_right !== 'not-allowed' ||
        store.user().admin_business_introducer_right !== 'not-allowed' ||
        store.user().admin_job_right !== 'not-allowed' ||
        store.user().admin_model_right !== 'not-allowed' ||
        store.user().admin_plaque_right !== 'not-allowed' ||
        store.user().admin_phone_book_right !== 'not-allowed' ||
        store.user().admin_user_account_right !== 'not-allowed' ||
        store.user().admin_user_tracking_right !== 'not-allowed',
    ),
  })),
);
